import { forwardRef, type ElementType, type ReactNode, type Ref, useEffect } from 'react';

const LOCK_ATTR = 'data-scroll-locked';

function useBodyScrollLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const current = parseInt(document.body.getAttribute(LOCK_ATTR) ?? '0', 10);
    const next = (Number.isFinite(current) ? current : 0) + 1;
    document.body.setAttribute(LOCK_ATTR, String(next));
    return () => {
      const after = parseInt(document.body.getAttribute(LOCK_ATTR) ?? '0', 10) - 1;
      if (after <= 0) {
        document.body.removeAttribute(LOCK_ATTR);
      } else {
        document.body.setAttribute(LOCK_ATTR, String(after));
      }
    };
  }, [enabled]);
}

type RemoveScrollProps = {
  readonly allowPinchZoom?: boolean;
  readonly as?: ElementType;
  readonly children?: ReactNode;
  readonly enabled?: boolean;
  readonly forwardProps?: boolean;
  readonly removeScrollBar?: boolean;
};

/**
 * CSP-safe stand-in for `react-remove-scroll` (FND-10): keeps pointer/touch handlers delegated
 * to Radix while applying scroll lock only through `body[data-scroll-locked]` + styles.css.
 */
export const RemoveScroll = forwardRef(function RemoveScroll(
  { as: Container = 'div', children, enabled = true, ...rest }: RemoveScrollProps,
  ref: Ref<HTMLElement>,
) {
  useBodyScrollLock(enabled);
  const {
    allowPinchZoom: _pinch,
    forwardProps: _forward,
    removeScrollBar: _bar,
    ...domProps
  } = rest;
  return (
    <Container {...domProps} ref={ref}>
      {children}
    </Container>
  );
});

RemoveScroll.displayName = 'RemoveScroll';

export const fullWidthClassName = 'width-before-scroll-bar';
export const zeroRightClassName = 'right-scroll-bar-position';

const removeScrollWithClassNames = RemoveScroll as typeof RemoveScroll & {
  classNames: { fullWidth: string; zeroRight: string };
};
removeScrollWithClassNames.classNames = {
  fullWidth: fullWidthClassName,
  zeroRight: zeroRightClassName,
};
